import React, { useMemo, useState, useEffect, useRef } from "react";
import PhoneInput, {
  isValidPhoneNumber,
  getCountries,
  getCountryCallingCode,
} from "react-phone-number-input";
import enLocale from "react-phone-number-input/locale/en.json";
import { Check, ChevronDown } from "lucide-react";
import "react-phone-number-input/style.css";

import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { cn } from "@/lib/utils";

// Neutral default when the user hasn't picked anything yet. We use "GB" as
// the *display* seed for the phone input (matches OrangeBySite's home market)
// but nothing is submitted until the user actively enters a number.
const DEFAULT_PHONE_COUNTRY = "GB";

// ISO alpha-2 code → 🇬🇧 emoji flag via regional indicator symbols.
// One canonical converter — never hand-maintained flag list.
export function countryFlagEmoji(code) {
  if (!code || code.length !== 2) return "";
  const cc = code.toUpperCase();
  const A = 0x1f1e6;
  const a = "A".charCodeAt(0);
  return String.fromCodePoint(A + cc.charCodeAt(0) - a,
                                A + cc.charCodeAt(1) - a);
}

// Precompute a sorted, searchable country list once per module load.
const COUNTRIES = getCountries()
  .map((code) => ({
    code,
    name: enLocale[code] || code,
    dialCode: getCountryCallingCode(code),
    flag: countryFlagEmoji(code),
  }))
  .sort((a, b) => a.name.localeCompare(b.name));

const inputCls =
  "w-full px-4 py-3 rounded-xl border-2 border-[#EAEAEA] bg-white text-[#0A0A0A] placeholder:text-[#8A8A8A] text-[14px] focus:outline-none focus:border-[#FF6A00] focus:ring-4 focus:ring-[#FF6A00]/20 transition-colors";

/**
 * Country dropdown — searchable, keyboard accessible, ISO-2 code storage.
 * Displays full country name; emits { code, name } via onChange.
 */
export function CountrySelect({ value, onChange, placeholder = "Select country", testId = "checkout-country" }) {
  const [open, setOpen] = useState(false);
  const selected = COUNTRIES.find((c) => c.code === value);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          role="combobox"
          aria-expanded={open}
          data-testid={testId}
          className={cn(inputCls, "flex items-center justify-between text-left")}
        >
          <span className={cn("flex items-center gap-2 truncate",
                              selected ? "" : "text-[#8A8A8A]")}>
            {selected ? (
              <>
                <span aria-hidden className="text-base leading-none">
                  {selected.flag}
                </span>
                <span className="truncate">{selected.name}</span>
              </>
            ) : (
              placeholder
            )}
          </span>
          <ChevronDown className="h-4 w-4 opacity-50 shrink-0 ml-2" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        className="p-0 w-[min(92vw,var(--radix-popover-trigger-width))] max-w-[92vw]"
        align="start"
      >
        <Command>
          <CommandInput placeholder="Search country…" data-testid={`${testId}-search`} />
          <CommandList className="max-h-64">
            <CommandEmpty>No country found.</CommandEmpty>
            <CommandGroup>
              {COUNTRIES.map((c) => (
                <CommandItem
                  key={c.code}
                  value={`${c.name} ${c.code} +${c.dialCode}`}
                  onSelect={() => {
                    onChange({ code: c.code, name: c.name });
                    setOpen(false);
                  }}
                  data-testid={`${testId}-item-${c.code}`}
                >
                  <Check className={cn("mr-2 h-4 w-4", value === c.code ? "opacity-100" : "opacity-0")} />
                  <span aria-hidden className="mr-2 text-base leading-none">{c.flag}</span>
                  <span className="flex-1 truncate">{c.name}</span>
                  <span className="ml-2 text-xs text-black/50 tabular-nums">+{c.dialCode}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

/**
 * PhoneCountryFields — encapsulates both fields with soft sync.
 *
 * Contract:
 *   value = { phone: string|"", country: string|"", country_code: string|"" }
 *   onChange(next)
 *
 * Storage:
 *   phone         → E.164 (e.g. "+447700900123") or "" if invalid/empty
 *   country       → full country name (backward-compat display value)
 *   country_code  → ISO alpha-2 (e.g. "GB")
 *
 * Phase 3G.12 hardening — the phone-input's country is now DERIVED from
 * `value.country_code` (no drift-prone local state) AND the PhoneInput is
 * force-remounted via `key` whenever the parent country changes.
 *
 * Phase 3I.3F fix — when the operator picks a NEW Country whose calling
 * code doesn't match the existing E.164, the national portion is cleared
 * so the phone input reflects the new country's flag. E.164 values that
 * already belong to the picked country are preserved verbatim.
 */
export function PhoneCountryFields({ value, onChange, disabled = false }) {
  // Derived — no local state to drift. `key` on PhoneInput forces a
  // clean remount whenever the parent's country_code changes.
  const phoneCountry = value.country_code || DEFAULT_PHONE_COUNTRY;
  const [phoneTouched, setPhoneTouched] = useState(false);
  const countryUserPicked = useRef(!!value.country_code);

  const phoneError = useMemo(() => {
    if (!phoneTouched) return "";
    if (!value.phone) return "";
    if (/[a-zA-Z]/.test(value.phone)) return "Please enter digits only.";
    return isValidPhoneNumber(value.phone) ? "" : "That doesn't look like a valid phone number.";
  }, [value.phone, phoneTouched]);

  function onCountryPicked({ code, name }) {
    countryUserPicked.current = true;
    // If the existing E.164 phone doesn't start with the new country's
    // calling code, clear it — the operator has explicitly moved the
    // client to a different country. Never silently convert +44… → +971….
    const nextCallingCode = code ? `+${getCountryCallingCode(code)}` : "";
    const phoneBelongsToNewCountry =
      !!value.phone && nextCallingCode && value.phone.startsWith(nextCallingCode);
    onChange({
      ...value,
      country: name,
      country_code: code,
      phone: phoneBelongsToNewCountry ? value.phone : "",
    });
    setPhoneTouched(false);
  }

  function onPhoneChanged(next) {
    // next is E.164 (or undefined). Coerce to string.
    onChange({ ...value, phone: next || "" });
  }

  function onPhoneCountryChanged(code) {
    // Only auto-fill the visible Country if the operator hasn't picked
    // one explicitly. Prevents the phone parser from silently
    // overwriting a user-picked country_code.
    if (code && !countryUserPicked.current) {
      onChange({
        ...value,
        country: enLocale[code] || code,
        country_code: code,
      });
    }
  }

  return (
    <div className="grid grid-cols-1 gap-3">
      <div>
        <div className="text-[10px] tracking-[0.24em] uppercase font-bold text-[#0A0A0A]/70 mb-1">
          Country <span className="text-black/40">(optional)</span>
        </div>
        <CountrySelect
          value={value.country_code || ""}
          onChange={onCountryPicked}
          testId="checkout-country"
        />
      </div>

      <div>
        <div className="text-[10px] tracking-[0.24em] uppercase font-bold text-[#0A0A0A]/70 mb-1">
          Phone <span className="text-black/40">(optional)</span>
        </div>
        <div
          data-testid="checkout-phone-wrapper"
          className={cn(
            "obs-phone-field",
            phoneError && "obs-phone-field--error"
          )}
        >
          <PhoneInput
            key={phoneCountry}
            international
            countryCallingCodeEditable={false}
            defaultCountry={phoneCountry}
            value={value.phone || ""}
            onChange={onPhoneChanged}
            onCountryChange={onPhoneCountryChanged}
            onBlur={() => setPhoneTouched(true)}
            disabled={disabled}
            autoComplete="tel"
            placeholder="Local number — we'll add the country code"
            numberInputProps={{
              "data-testid": "checkout-phone",
              className: "obs-phone-input",
              inputMode: "tel",
            }}
          />
        </div>
        {phoneError && (
          <div
            className="mt-1.5 text-[12px] text-[#C1350B] font-semibold"
            data-testid="checkout-phone-error"
          >
            {phoneError}
          </div>
        )}
      </div>
    </div>
  );
}

/** Exposed for form-level submit gating. */
export function isPhoneValidOrEmpty(phone) {
  if (!phone) return true;
  if (/[a-zA-Z]/.test(phone)) return false;
  return isValidPhoneNumber(phone);
}
